import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  AppointmentResponse,
  AvailabilityOverrideResponse,
  AvailabilityTemplateResponse,
  BranchResponse,
  CreateOverrideRequest,
  DayOfWeek,
  OverrideResultResponse,
  ReplaceWeeklyTemplateRequest,
  StaffMemberResponse,
  WorkingHoursDay,
} from '../../api/types';
import { AsyncSection } from '../../components/AsyncSection';
import { Icon } from '../../components/Icon';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState } from '../../components/states/StateViews';
import { useAuthStore } from '../../auth/authStore';
import { useLocalisedName } from '../../i18n/localisedName';
import { AffectedBookings } from '../scheduling/AffectedBookings';
import { clinicToday, longDate, shiftDay } from '../scheduling/clinicTime';
import { useBranchPicker } from '../scheduling/useBranchPicker';
import {
  WEEK_DAYS,
  currentTemplates,
  datesBetween,
  elsewhereOn,
  firstBlock,
  formatBlock,
  hhmm,
  outsideBranch,
  problemsOn,
  sameWeek,
  toRequest,
  weekAt,
  weekHasProblems,
  type Block,
  type ElsewhereBlock,
  type Week,
} from './week';

/** How far ahead the list of days off and extra hours reaches. */
const AHEAD_DAYS = 365;

/** The longest absence entered in one go. Each day is its own exception, and its own worklist. */
const MAX_DAYS_AT_ONCE = 31;

type AwayKind = 'dayOff' | 'away' | 'extra';

/**
 * Doctors' working hours, entered by the desk (FR-702, D-2).
 *
 * Without these nobody can be booked: the slot finder offers a doctor only inside their weekly
 * hours, clipped to the branch's opening hours, minus their days off. A doctor works for the whole
 * clinic (ASM-009) but keeps a separate week at each branch, so the week is edited one branch at a
 * time, with the doctor's hours at the other branches shown beside it and refused where they clash.
 *
 * Someone who works at one branch (a branch manager) is offered only that branch, as on the day
 * board (CR-17), and records absences there rather than for the whole clinic; the server holds
 * them to it (CR-20).
 *
 * A new week that would leave bookings outside the doctor's hours is shown with those bookings
 * before it is saved. Saving keeps them booked and puts them on a worklist (CR-20, DEC-031).
 */
export function DoctorHoursPage() {
  const { t } = useTranslation();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const ownBranchId = useAuthStore(
    (state) =>
      state.principal?.memberships?.find((membership) => membership.clinicId === state.activeClinicId)?.branchId ??
      null,
  );

  const staff = useApiQuery<StaffMemberResponse[]>(
    queryKeys.staff(clinicId ?? ''),
    `/clinics/${clinicId}/staff`,
    { enabled: Boolean(clinicId) },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.doctorHours')} description={t('home.noClinic')} />;
  }

  return (
    <>
      <PageHeader
        title={t('nav.items.doctorHours')}
        description={ownBranchId ? t('doctorHours.descriptionBranch') : t('doctorHours.description')}
      />
      <AsyncSection
        query={staff}
        skeletonRows={4}
        isEmpty={(rows) => doctorsOf(rows).length === 0}
        emptyTitle={t('doctorHours.noDoctors')}
        emptyDescription={t('doctorHours.noDoctorsDescription')}
      >
        {(rows) => <DoctorCalendars clinicId={clinicId} doctors={doctorsOf(rows)} ownBranchId={ownBranchId} />}
      </AsyncSection>
    </>
  );
}

function doctorsOf(rows: readonly StaffMemberResponse[]): StaffMemberResponse[] {
  return rows.filter((member) => member.role === 'DOCTOR' && member.employmentStatus !== 'ENDED' && member.id);
}

/** The doctor and the branch being looked at. The doctor is in the address, so Staff can link here. */
function DoctorCalendars({
  clinicId,
  doctors,
  ownBranchId,
}: {
  readonly clinicId: string;
  readonly doctors: readonly StaffMemberResponse[];
  readonly ownBranchId: string | null;
}) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const [params, setParams] = useSearchParams();
  const picker = useBranchPicker();
  const branches = useApiQuery<BranchResponse[]>(queryKeys.branches(clinicId), `/clinics/${clinicId}/branches`);

  const doctor = doctors.find((candidate) => candidate.id === params.get('doctor')) ?? doctors[0];
  if (!doctor?.id) {
    return null;
  }

  return (
    <>
      <div className="toolbar">
        <label className="field">
          <span className="field__label">{t('doctorHours.doctor')}</span>
          <select
            className="field__input"
            value={doctor.id}
            onChange={(event) => setParams({ doctor: event.target.value }, { replace: true })}
          >
            {doctors.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.displayName ?? candidate.email}
              </option>
            ))}
          </select>
        </label>
        {picker.branches.length > 0 ? (
          <label className="field">
            <span className="field__label">{t('doctorHours.branch')}</span>
            <select
              className="field__input"
              value={picker.branchId ?? ''}
              disabled={picker.branches.length < 2}
              onChange={(event) => picker.choose(event.target.value)}
            >
              {picker.branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {localised.name(branch, branch.code)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <DoctorCalendar
        key={doctor.id}
        clinicId={clinicId}
        employmentId={doctor.id}
        branchId={picker.branchId}
        noBranches={!picker.isLoading && picker.branches.length === 0}
        branches={branches.data ?? []}
        offered={picker.branches}
        ownBranchId={ownBranchId}
      />
    </>
  );
}

function DoctorCalendar({
  clinicId,
  employmentId,
  branchId,
  noBranches,
  branches,
  offered,
  ownBranchId,
}: {
  readonly clinicId: string;
  readonly employmentId: string;
  readonly branchId: string | null;
  readonly noBranches: boolean;
  readonly branches: readonly BranchResponse[];
  readonly offered: readonly BranchResponse[];
  readonly ownBranchId: string | null;
}) {
  const { t } = useTranslation();
  const localised = useLocalisedName();
  const canManage = useAuthStore((state) => state.permissions.has('AVAILABILITY_MANAGE_ANY'));
  const today = clinicToday();

  const templates = useApiQuery<AvailabilityTemplateResponse[]>(
    queryKeys.doctorAvailability(clinicId, employmentId),
    `/clinics/${clinicId}/doctors/${employmentId}/availability`,
  );

  const branchName = (id: string | undefined) => {
    const branch = branches.find((candidate) => candidate.id === id);
    return branch ? localised.name(branch, branch.code) : '—';
  };

  return (
    <>
      <section className="section" aria-labelledby="doctor-week-heading">
        <h2 className="section__title" id="doctor-week-heading">
          {t('doctorHours.weekTitle')}
        </h2>
        {branchId ? (
          <AsyncSection query={templates} skeletonRows={7}>
            {(rows) => (
              <WeekEditor
                key={branchId}
                clinicId={clinicId}
                employmentId={employmentId}
                branchId={branchId}
                rows={currentTemplates(rows, today)}
                branchName={branchName}
                canManage={canManage}
              />
            )}
          </AsyncSection>
        ) : noBranches ? (
          <p className="field__hint">{t('doctorHours.noBranches')}</p>
        ) : null}
      </section>

      <AwaySection
        clinicId={clinicId}
        employmentId={employmentId}
        today={today}
        offered={offered}
        ownBranchId={ownBranchId}
        defaultBranchId={branchId}
        branchName={branchName}
        canManage={canManage}
      />
    </>
  );
}

// ── The week at one branch ──────────────────────────────────────────────────

function WeekEditor({
  clinicId,
  employmentId,
  branchId,
  rows,
  branchName,
  canManage,
}: {
  readonly clinicId: string;
  readonly employmentId: string;
  readonly branchId: string;
  readonly rows: readonly AvailabilityTemplateResponse[];
  readonly branchName: (id: string | undefined) => string;
  readonly canManage: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Week | null>(null);
  const [saved, setSaved] = useState(false);
  // The week waiting on the desk's answer, and the bookings it would leave outside the hours.
  const [confirming, setConfirming] = useState<{
    readonly request: ReplaceWeeklyTemplateRequest;
    readonly affected: AppointmentResponse[];
  } | null>(null);
  // How many bookings the last save put on a worklist, so the confirmation can say so.
  const [listed, setListed] = useState(0);

  const hours = useApiQuery<WorkingHoursDay[]>(
    queryKeys.workingHours(clinicId, branchId),
    `/clinics/${clinicId}/branches/${branchId}/working-hours`,
  );

  const check = useApiMutation<AppointmentResponse[], ReplaceWeeklyTemplateRequest>((body) => ({
    path: `/clinics/${clinicId}/doctors/${employmentId}/availability/impact`,
    options: { method: 'POST', body },
  }));

  // The draft is kept after saving rather than dropped, so the table does not flash back to the
  // old week while the saved one is fetched; it simply stops counting as a change once it arrives.
  const save = useApiMutation<AvailabilityTemplateResponse[], ReplaceWeeklyTemplateRequest>(
    (body) => ({ path: `/clinics/${clinicId}/doctors/${employmentId}/availability`, options: { method: 'PUT', body } }),
    {
      invalidates: [queryKeys.doctorAvailability(clinicId, employmentId), ...scheduleDependents(clinicId)],
      onSuccess: () => {
        setSaved(true);
        setConfirming(null);
      },
    },
  );

  const stored = weekAt(rows, branchId);
  const week = draft ?? stored;
  const elsewhere = (day: DayOfWeek) => elsewhereOn(rows, branchId, day);
  const hoursOn = (day: DayOfWeek) => hours.data?.find((entry) => entry.day === day);
  const changed = draft !== null && !sameWeek(draft, stored);
  const blocked = weekHasProblems(week, elsewhere);

  const change = (day: DayOfWeek, blocks: readonly Block[]) => {
    setDraft({ ...week, [day]: blocks });
    setSaved(false);
    setConfirming(null);
  };

  // Asks first, as a branch's week does (CR-12). A week that strands nobody saves straight away;
  // one that leaves bookings outside the doctor's hours waits until the desk has seen who (CR-20).
  const start = async () => {
    const request = toRequest(branchId, week);
    setListed(0);
    try {
      const affected = await check.mutateAsync(request);
      if (affected.length === 0) {
        save.mutate(request);
      } else {
        setConfirming({ request, affected });
      }
    } catch {
      // Shown below from check.error.
    }
  };

  return (
    <>
      <p className="field__hint" style={{ marginBlockEnd: 'var(--space-3)' }}>
        {t('doctorHours.weekHint')}
      </p>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t('doctorHours.day')}</th>
              <th scope="col">{t('doctorHours.hours')}</th>
            </tr>
          </thead>
          <tbody>
            {WEEK_DAYS.map((day) => (
              <DayRow
                key={day}
                day={day}
                blocks={week[day]}
                elsewhere={elsewhere(day)}
                branchHours={hoursOn(day)}
                branchName={branchName}
                canManage={canManage}
                onChange={(blocks) => change(day, blocks)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {check.error ? <ErrorState error={check.error} /> : null}

      {confirming ? (
        <AffectedBookings
          affected={confirming.affected}
          title={t('doctorHours.affected.title', { count: confirming.affected.length })}
          explanation={t('doctorHours.affected.explanation')}
          confirmLabel={t('doctorHours.affected.confirm', { count: confirming.affected.length })}
          destructive={false}
          pending={save.isPending}
          error={save.error}
          onConfirm={() => {
            const count = confirming.affected.length;
            save.mutate(confirming.request, { onSuccess: () => setListed(count) });
          }}
          onBack={() => setConfirming(null)}
        />
      ) : (
        <>
          {save.error ? <ErrorState error={save.error} /> : null}
          {canManage ? (
            <div className="toolbar" style={{ marginBlockStart: 'var(--space-4)' }}>
              <button
                type="button"
                className="button button--primary"
                disabled={!changed || blocked || check.isPending || save.isPending}
                onClick={() => void start()}
              >
                {check.isPending || save.isPending ? t('doctorHours.saving') : t('doctorHours.saveWeek')}
              </button>
              <button
                type="button"
                className="button button--ghost"
                disabled={!changed || save.isPending}
                onClick={() => setDraft(null)}
              >
                {t('common.cancel')}
              </button>
              {saved && !changed ? (
                <span className="field__hint" role="status">
                  {listed > 0 ? t('doctorHours.affected.done', { count: listed }) : t('doctorHours.weekSaved')}
                </span>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

function DayRow({
  day,
  blocks,
  elsewhere,
  branchHours,
  branchName,
  canManage,
  onChange,
}: {
  readonly day: DayOfWeek;
  readonly blocks: readonly Block[];
  readonly elsewhere: readonly ElsewhereBlock[];
  readonly branchHours: WorkingHoursDay | undefined;
  readonly branchName: (id: string | undefined) => string;
  readonly canManage: boolean;
  readonly onChange: (blocks: readonly Block[]) => void;
}) {
  const { t } = useTranslation();
  const dayName = t(`branches.days.${day}`);
  const problems = problemsOn(blocks, elsewhere);
  const place = outsideBranch(blocks, branchHours);

  const update = (index: number, change: Partial<Block>) =>
    onChange(blocks.map((block, at) => (at === index ? { ...block, ...change } : block)));

  // Grouped by branch, so a split shift elsewhere reads as one line.
  const otherBranches = [...new Set(elsewhere.map((block) => block.branchId))];

  return (
    <tr>
      <th scope="row">{dayName}</th>
      <td>
        <div className="week-blocks">
          {blocks.length === 0 ? <span className="muted">{t('doctorHours.notWorking')}</span> : null}
          {blocks.map((block, index) => (
            <span className="week-blocks__block" key={index}>
              <input
                type="time"
                className="field__input numeric"
                value={block.startsAt}
                disabled={!canManage}
                aria-label={t('doctorHours.starts', { day: dayName })}
                onChange={(event) => update(index, { startsAt: event.target.value })}
              />
              <span aria-hidden="true">–</span>
              <input
                type="time"
                className="field__input numeric"
                value={block.endsAt}
                disabled={!canManage}
                aria-label={t('doctorHours.ends', { day: dayName })}
                onChange={(event) => update(index, { endsAt: event.target.value })}
              />
              {canManage ? (
                <button
                  type="button"
                  className="button button--ghost button--icon"
                  aria-label={t('doctorHours.removeBlock', { day: dayName, hours: formatBlock(block) })}
                  onClick={() => onChange(blocks.filter((_, at) => at !== index))}
                >
                  <Icon name="close" size={16} />
                </button>
              ) : null}
            </span>
          ))}
          {canManage ? (
            <button
              type="button"
              className="button button--ghost button--sm"
              aria-label={t('doctorHours.addBlockOn', { day: dayName })}
              onClick={() =>
                onChange([...blocks, blocks.length === 0 ? firstBlock(branchHours) : { startsAt: '', endsAt: '' }])
              }
            >
              {t('doctorHours.addBlock')}
            </button>
          ) : null}
        </div>

        {problems.map((problem, index) => (
          <p key={index} className="field__error">
            {problem.kind === 'clash'
              ? t('doctorHours.problems.clash', {
                  branch: branchName(problem.branchId),
                  hours: formatBlock(problem.block),
                })
              : t(`doctorHours.problems.${problem.kind}`)}
          </p>
        ))}
        {place === 'closed' ? (
          <p className="field__hint">{t('doctorHours.branchClosed')}</p>
        ) : place === 'outside' && branchHours ? (
          <p className="field__hint">
            {t('doctorHours.outsideBranch', { opens: hhmm(branchHours.opensAt), closes: hhmm(branchHours.closesAt) })}
          </p>
        ) : null}
        {otherBranches.map((otherId) => (
          <p key={otherId} className="field__hint">
            {t('doctorHours.elsewhere', {
              branch: branchName(otherId),
              hours: elsewhere
                .filter((block) => block.branchId === otherId)
                .map(formatBlock)
                .join(', '),
            })}
          </p>
        ))}
      </td>
    </tr>
  );
}

// ── Days off and extra hours ────────────────────────────────────────────────

/**
 * One-date exceptions to the week: a day off, a few hours away, or an extra evening.
 *
 * Time off over bookings does not cancel them. The server leaves them standing and opens a worklist
 * for reception to call the patients (FR-306, DEC-031), and says how many; so does this screen.
 */
function AwaySection({
  clinicId,
  employmentId,
  today,
  offered,
  ownBranchId,
  defaultBranchId,
  branchName,
  canManage,
}: {
  readonly clinicId: string;
  readonly employmentId: string;
  readonly today: string;
  readonly offered: readonly BranchResponse[];
  readonly ownBranchId: string | null;
  readonly defaultBranchId: string | null;
  readonly branchName: (id: string | undefined) => string;
  readonly canManage: boolean;
}) {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const formRef = useRef<HTMLFormElement>(null);
  const [kind, setKind] = useState<AwayKind>('dayOff');
  const [problem, setProblem] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState<{
    readonly saved: number;
    readonly total: number;
    readonly affected: number;
    readonly failed: boolean;
  } | null>(null);

  const invalidates = [queryKeys.doctorOverrides(clinicId, employmentId), ...scheduleDependents(clinicId)];

  const overrides = useApiQuery<AvailabilityOverrideResponse[]>(
    queryKeys.doctorOverrides(clinicId, employmentId),
    `/clinics/${clinicId}/doctors/${employmentId}/overrides?from=${today}&to=${shiftDay(today, AHEAD_DAYS)}`,
  );

  const add = useApiMutation<OverrideResultResponse, CreateOverrideRequest>(
    (body) => ({ path: `/clinics/${clinicId}/doctors/${employmentId}/overrides`, options: { method: 'POST', body } }),
    { invalidates },
  );

  const remove = useApiMutation<void, string>(
    (overrideId) => ({
      path: `/clinics/${clinicId}/doctors/availability-overrides/${overrideId}`,
      options: { method: 'DELETE' },
    }),
    { invalidates },
  );

  // One request per date, one after another, so a failure part-way says exactly how far it got.
  const submit = async (form: FormData) => {
    setProblem(null);
    setOutcome(null);
    const from = String(form.get('from') ?? '');
    const until = (kind === 'dayOff' && String(form.get('until') ?? '')) || from;
    const startsAt = kind === 'dayOff' ? undefined : String(form.get('startsAt') ?? '');
    const endsAt = kind === 'dayOff' ? undefined : String(form.get('endsAt') ?? '');
    const where = String(form.get('where') ?? '');

    if (!from) {
      return;
    }
    if (until < from) {
      setProblem(t('doctorHours.away.untilBeforeFrom'));
      return;
    }
    const dates = datesBetween(from, until);
    if (dates.length > MAX_DAYS_AT_ONCE) {
      setProblem(t('doctorHours.away.tooLong', { n: MAX_DAYS_AT_ONCE }));
      return;
    }
    if (startsAt !== undefined && (!startsAt || !endsAt || endsAt <= startsAt)) {
      setProblem(t('doctorHours.away.timesOrder'));
      return;
    }

    setRunning(true);
    let saved = 0;
    let affected = 0;
    let failed = false;
    for (const date of dates) {
      try {
        const result = await add.mutateAsync({
          branchId: where || undefined,
          overrideOn: date,
          kind: kind === 'extra' ? 'AVAILABLE' : 'UNAVAILABLE',
          startsAt: startsAt ? `${startsAt}:00` : undefined,
          endsAt: endsAt ? `${endsAt}:00` : undefined,
          reason: String(form.get('reason') ?? '').trim() || undefined,
        });
        saved += 1;
        affected += result.affectedAppointments ?? 0;
      } catch {
        failed = true;
        break;
      }
    }
    setRunning(false);
    setOutcome({ saved, total: dates.length, affected, failed });
    if (!failed) {
      // A reset puts the kind back on its first option, so the state follows it there.
      formRef.current?.reset();
      setKind('dayOff');
    }
  };

  const describe = (entry: AvailabilityOverrideResponse) => {
    const hours = formatBlock({ startsAt: hhmm(entry.startsAt), endsAt: hhmm(entry.endsAt) });
    if (entry.kind === 'AVAILABLE') {
      return t('doctorHours.away.extraHours', { hours });
    }
    return entry.startsAt && entry.endsAt ? t('doctorHours.away.awayHours', { hours }) : t('doctorHours.away.dayOff');
  };

  // A branch manager looks after their own branch: they may lift an exception there, not one that
  // covers the whole clinic or another branch.
  const removable = (entry: AvailabilityOverrideResponse) => !ownBranchId || entry.branchId === ownBranchId;

  // Extra hours happen somewhere in particular, and a branch manager records time off at their own
  // branch, so "all branches" is offered only for the clinic administrator's time off.
  const offerAll = !ownBranchId && kind !== 'extra';
  const defaultWhere = offerAll ? '' : (ownBranchId ?? defaultBranchId ?? offered[0]?.id ?? '');

  return (
    <section className="section" aria-labelledby="doctor-away-heading">
      <h2 className="section__title" id="doctor-away-heading">
        {t('doctorHours.awayTitle')}
      </h2>

      <AsyncSection
        query={overrides}
        isEmpty={(rows) => rows.length === 0}
        emptyTitle={t('doctorHours.away.emptyTitle')}
        emptyDescription={t('doctorHours.away.emptyDescription')}
      >
        {(rows) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t('doctorHours.away.date')}</th>
                  <th scope="col">{t('doctorHours.away.where')}</th>
                  <th scope="col">{t('doctorHours.away.what')}</th>
                  <th scope="col">{t('doctorHours.away.reason')}</th>
                  {canManage ? <th scope="col">{t('common.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => (
                  <tr key={entry.id}>
                    <th scope="row">{entry.overrideOn ? longDate(entry.overrideOn, i18n.language) : '—'}</th>
                    <td>{entry.branchId ? branchName(entry.branchId) : t('doctorHours.away.allBranches')}</td>
                    <td>{describe(entry)}</td>
                    <td>{entry.reason ?? '—'}</td>
                    {canManage ? (
                      <td>
                        {removable(entry) ? (
                          <button
                            type="button"
                            className="button button--ghost"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(entry.id ?? '')}
                          >
                            {t('doctorHours.away.remove')}
                          </button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {remove.error ? <ErrorState error={remove.error} /> : null}

      {canManage ? (
        <form
          ref={formRef}
          className="toolbar"
          style={{ marginBlockStart: 'var(--space-4)' }}
          onSubmit={(event) => {
            event.preventDefault();
            void submit(new FormData(event.currentTarget));
          }}
        >
          <label className="field">
            <span className="field__label">{t('doctorHours.away.what')}</span>
            <select
              name="kind"
              className="field__input"
              value={kind}
              onChange={(event) => setKind(event.target.value as AwayKind)}
            >
              {(['dayOff', 'away', 'extra'] as const).map((option) => (
                <option key={option} value={option}>
                  {t(`doctorHours.away.kinds.${option}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">
              {kind === 'dayOff' ? t('doctorHours.away.from') : t('doctorHours.away.on')}
            </span>
            <input type="date" name="from" className="field__input numeric" min={today} required />
          </label>
          {kind === 'dayOff' ? (
            <label className="field">
              <span className="field__label">{t('doctorHours.away.until')}</span>
              <input type="date" name="until" className="field__input numeric" min={today} />
              <span className="field__hint">{t('doctorHours.away.untilHint', { n: MAX_DAYS_AT_ONCE })}</span>
            </label>
          ) : (
            <>
              <label className="field">
                <span className="field__label">{t('doctorHours.away.startsAt')}</span>
                <input type="time" name="startsAt" className="field__input numeric" required />
              </label>
              <label className="field">
                <span className="field__label">{t('doctorHours.away.endsAt')}</span>
                <input type="time" name="endsAt" className="field__input numeric" required />
              </label>
            </>
          )}
          <label className="field">
            <span className="field__label">{t('doctorHours.away.where')}</span>
            <select key={kind} name="where" className="field__input" defaultValue={defaultWhere}>
              {offerAll ? <option value="">{t('doctorHours.away.allBranches')}</option> : null}
              {offered.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {localised.name(branch, branch.code)}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field__label">{t('doctorHours.away.reasonOptional')}</span>
            <input name="reason" className="field__input" maxLength={400} />
          </label>
          <button type="submit" className="button button--ghost" disabled={running}>
            {running ? t('doctorHours.saving') : t('doctorHours.away.add')}
          </button>
        </form>
      ) : null}

      {problem ? <p className="field__error">{problem}</p> : null}

      {outcome && outcome.saved > 0 && !outcome.failed ? (
        <p className="field__hint" role="status">
          {t('doctorHours.away.saved', { count: outcome.saved })}
          {outcome.affected > 0 ? ` ${t('doctorHours.away.affected', { count: outcome.affected })}` : ''}
        </p>
      ) : null}

      {outcome?.failed ? (
        <>
          {outcome.saved > 0 ? (
            <p className="field__error">
              {t('doctorHours.away.partial', { done: outcome.saved, total: outcome.total })}
              {outcome.affected > 0 ? ` ${t('doctorHours.away.affected', { count: outcome.affected })}` : ''}
            </p>
          ) : null}
          <ErrorState error={add.error} />
        </>
      ) : null}
    </section>
  );
}

/**
 * What a change to a doctor's calendar can touch besides the calendar: the free times offered for
 * booking, the day board's columns, and the worklist an absence over bookings opens.
 */
function scheduleDependents(clinicId: string): readonly (readonly unknown[])[] {
  return [
    ['clinics', clinicId, 'doctors'],
    ['clinics', clinicId, 'availability'],
    ['clinics', clinicId, 'appointments'],
    ['clinics', clinicId, 'worklists'],
  ];
}
